import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';

// Length units → meters
const L = { in:0.0254, ft:0.3048, yd:0.9144, m:1, cm:0.01, mm:0.001 };

export default function TileCalc() {
  const [areaL, setAreaL] = useState(10);
  const [areaW, setAreaW] = useState(8);
  const [unit, setUnit] = useState('ft');
  const [tileL, setTileL] = useState(12);
  const [tileW, setTileW] = useState(12);
  const [tileUnit, setTileUnit] = useState('in');
  const [gap, setGap] = useState(0.125);
  const [gapUnit, setGapUnit] = useState('in');
  const [waste, setWaste] = useState(10);
  const [price, setPrice] = useState(2.50);

  const out = useMemo(() => {
    const areaM2 = Number(areaL) * Number(areaW) * (L[unit] ** 2);
    const tL = Number(tileL) * L[tileUnit] + Number(gap) * L[gapUnit];
    const tW = Number(tileW) * L[tileUnit] + Number(gap) * L[gapUnit];
    const tileArea = tL * tW;
    const need = tileArea ? Math.ceil((areaM2 / tileArea) * (1 + Number(waste)/100)) : 0;
    const cost = need * Number(price);
    return { areaM2, tileArea, need, cost };
  }, [areaL, areaW, unit, tileL, tileW, tileUnit, gap, gapUnit, waste, price]);

  return (
    <CalcShell look="cnet" title="Tile Calculator" subtitle="Estimate how many tiles you need for a room, accounting for grout gaps and waste.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="grid grid-cols-2 gap-3">
            <div className="field"><label>Room Length</label><input type="number" value={areaL} onChange={e => setAreaL(e.target.value)} data-testid="tile-al"/></div>
            <div className="field"><label>Room Width</label><input type="number" value={areaW} onChange={e => setAreaW(e.target.value)} data-testid="tile-aw"/></div>
          </div>
          <div className="field"><label>Room Unit</label>
            <select value={unit} onChange={e => setUnit(e.target.value)}>{Object.keys(L).map(u => <option key={u}>{u}</option>)}</select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="field"><label>Tile Length</label><input type="number" value={tileL} onChange={e => setTileL(e.target.value)}/></div>
            <div className="field"><label>Tile Width</label><input type="number" value={tileW} onChange={e => setTileW(e.target.value)}/></div>
          </div>
          <div className="field"><label>Tile Unit</label>
            <select value={tileUnit} onChange={e => setTileUnit(e.target.value)}>{Object.keys(L).map(u => <option key={u}>{u}</option>)}</select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="field"><label>Grout Gap</label><input type="number" step="0.01" value={gap} onChange={e => setGap(e.target.value)}/></div>
            <div className="field"><label>Gap Unit</label>
              <select value={gapUnit} onChange={e => setGapUnit(e.target.value)}>{Object.keys(L).map(u => <option key={u}>{u}</option>)}</select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="field"><label>Waste %</label><input type="number" value={waste} onChange={e => setWaste(e.target.value)}/></div>
            <div className="field"><label>Price / Tile</label><input type="number" step="0.01" value={price} onChange={e => setPrice(e.target.value)}/></div>
          </div>
        </div>
        <div>
          <div className="p-5 rounded-xl bg-orange-50 border border-orange-200 text-center">
            <div className="text-sm text-slate-600">Tiles Needed</div>
            <div className="text-4xl font-bold text-orange-700" data-testid="tile-count">{out.need}</div>
            <div className="text-sm text-slate-600 mt-3">Estimated Cost</div>
            <div className="text-2xl font-bold text-slate-900">${out.cost.toFixed(2)}</div>
          </div>
          <table className="data-table mt-4">
            <tbody>
              <tr><td>Room Area</td><td className="text-right">{out.areaM2.toFixed(3)} m²</td></tr>
              <tr><td>Effective Tile Area (with gap)</td><td className="text-right">{out.tileArea.toFixed(4)} m²</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </CalcShell>
  );
}
